import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { 
    Container, Grid, Typography, Box, Button, Paper, Divider, 
    Skeleton, Checkbox, Tabs, Tab, FormControlLabel 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowRight, faShoppingBag, faWallet, 
    faCheckCircle, faExclamationCircle, faStore 
} from '@fortawesome/free-solid-svg-icons';

// Componentes y Controladores
import CartItemRow from '../../../components/commerce/CartItemRow';
import { getMyCart, removeCartItem, updateCartItemQty } from '../../../controllers/commerce/CartController';
import CustomBreadcrumbs from '../../../components/breadcrumbs';

export default function CartPage() {
    const history = useHistory();
    
    const [cartData, setCartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0.00); 

    // --- NUEVOS ESTADOS PARA MULTI-TENANT ---
    const [activeCountry, setActiveCountry] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const resCart = await getMyCart();
        
        if (resCart.success && resCart.data && resCart.data.groups?.length > 0) {
            setCartData(resCart.data);
            
            // 1. Inicializamos el país activo con el primero de la lista
            const firstCountryId = resCart.data.groups[0].country_id;
            setActiveCountry(firstCountryId);

            // 2. Por defecto, seleccionamos TODOS los ítems de ese país
            const initialSelection = resCart.data.groups
                .filter(g => g.country_id === firstCountryId)
                .flatMap(g => g.items.map(i => i.item_id));
            
            setSelectedItems(initialSelection);
        } else {
            setCartData(null);
        }

        setWalletBalance(0.00); // Simulamos wallet por ahora
        setLoading(false);
    };

    // --- LÓGICA DE CHECKBOXES ---
    const handleToggleItem = (itemId) => {
        setSelectedItems(prev => 
            prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
        );
    };

    const handleToggleMerchant = (merchantGroupId, itemsList) => {
        const itemIds = itemsList.map(i => i.item_id);
        const allSelected = itemIds.every(id => selectedItems.includes(id));

        if (allSelected) {
            // Deseleccionar todos los del comercio
            setSelectedItems(prev => prev.filter(id => !itemIds.includes(id)));
        } else {
            // Seleccionar todos los del comercio (sin duplicar)
            setSelectedItems(prev => [...new Set([...prev, ...itemIds])]);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveCountry(newValue);
        // Al cambiar de país, seleccionamos automáticamente todos los de ese país
        const itemsInNewCountry = cartData.groups
            .filter(g => g.country_id === newValue)
            .flatMap(g => g.items.map(i => i.id));
        setSelectedItems(itemsInNewCountry);
    };

    // --- ACCIONES CRUD ---
    const handleRemoveItem = async (itemId) => {
        if(!window.confirm("¿Eliminar este producto?")) return;
        setProcessing(true);
        const res = await removeCartItem(itemId);
        if (res.success) {
            setSelectedItems(prev => prev.filter(id => id !== itemId));
            const updatedCart = await getMyCart();
            if (updatedCart.success && updatedCart.data.groups?.length > 0) {
                setCartData(updatedCart.data);
            } else {
                setCartData(null);
            }
        }
        setProcessing(false);
    };

    const handleUpdateQty = async (itemId, newQty) => {
        const res = await updateCartItemQty(itemId, newQty);
        if (res.success) {
            const updatedCart = await getMyCart();
            if (updatedCart.success) setCartData(updatedCart.data);
        }
    };

    const handleCheckout = () => {
        if (selectedItems.length === 0) {
            alert("Selecciona al menos un producto para proceder al pago.");
            return;
        }
        // Enviamos los IDs seleccionados al CheckoutPage
        history.push({
            pathname: '/checkout',
            state: { selectedItemIds: selectedItems }
        });
    };

    // --- RENDERIZADO DE CARGA ---
    if (loading && !cartData) {
        return (
            <Container maxWidth="xl" sx={{ pt: 12 }}>
                <CustomBreadcrumbs returnTo='/' crumbs={[{ name: 'Carrito', link: '/cart' }]} />
                <Grid container spacing={4} mt={2}>
                    <Grid item xs={12} md={8}><Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} /></Grid>
                    <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} /></Grid>
                </Grid>
            </Container>
        );
    }

    if (!cartData || cartData.groups.length === 0) {
        return (
            <Container maxWidth="xl" sx={{ pb: 8 }}>
                <Paper sx={{ p: 8, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: 4 }} elevation={0}>
                    <Box sx={{ color: 'text.disabled', mb: 3 }}>
                        <FontAwesomeIcon icon={faShoppingBag} size="4x" />
                    </Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Tu carrito está vacío</Typography>
                    <Button variant="contained" size="large" sx={{ borderRadius: 50, px: 5, mt: 2 }} onClick={() => history.push('/')}>
                        Comenzar a Comprar
                    </Button>
                </Paper>
            </Container>
        );
    }

    // --- PROCESAMIENTO DE DATOS VISUALES ---
    const groups = cartData.groups || [];
    
    // 1. Extraer países únicos para las Pestañas
    const countriesMap = new Map();
    groups.forEach(g => {
        if(!countriesMap.has(g.country_id)) {
            countriesMap.set(g.country_id, { 
                id: g.country_id, 
                name: g.country_name, 
                symbol: g.currency_symbol,
                code: g.currency_code // 🔥 Capturamos el código de la moneda
            });
        }
    });
    const countries = Array.from(countriesMap.values());
    const activeCountryData = countriesMap.get(activeCountry);
    const activeCurrencySymbol = activeCountryData?.symbol || '$';
    const activeCurrencyCode = activeCountryData?.code || 'USD';

    // 2. Filtrar comercios por país activo
    const activeGroups = groups.filter(g => g.country_id === activeCountry);

    // 3. Cálculos Dinámicos de Totales (Solo lo seleccionado)
    let calcSubtotal = 0;
    let calcDelivery = 0;

    activeGroups.forEach(group => {
        // 🔥 Usamos item_id
        const selectedInGroup = group.items.filter(item => selectedItems.includes(item.item_id));
        
        if (selectedInGroup.length > 0) {
            // 🔥 Usamos total_line que ya viene calculado desde Laravel
            const groupSub = selectedInGroup.reduce((acc, item) => acc + (parseFloat(item.total_line) || 0), 0);
            calcSubtotal += groupSub;
            calcDelivery += parseFloat(group.delivery_fee || 0);
        }
    });

    const totalToPay = calcSubtotal + calcDelivery;
    const currentBalance = parseFloat(walletBalance);
    const remainingBalance = currentBalance - totalToPay;
    const isSufficient = remainingBalance >= 0;

    return (
        <Container maxWidth="xl" sx={{ pb: 8 }}>
            <Box mb={2}>
                <CustomBreadcrumbs returnTo='/' crumbs={[{ name: 'Carrito', link: '/cart' }]} />
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 2 }}>
                    Mi Carrito
                </Typography>
            </Box>

            {/* PESTAÑAS POR PAÍS */}
            {countries.length > 1 && (
                <Tabs 
                    value={activeCountry} 
                    onChange={handleTabChange} 
                    textColor="primary" 
                    indicatorColor="primary"
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                >
                    {countries.map(c => (
                        <Tab key={c.id} value={c.id} label={`🛒 Compras en ${c.name}`} sx={{ fontWeight: 'bold' }} />
                    ))}
                </Tabs>
            )}

            <Grid container spacing={4}>
                {/* --- LISTA DE COMERCIOS (IZQUIERDA) --- */}
                <Grid item xs={12} md={8}>
                    {activeGroups.map((group) => {
                        const itemsInGroup = group.items.map(i => i.item_id);
                        const allSelected = itemsInGroup.every(id => selectedItems.includes(id));
                        const someSelected = itemsInGroup.some(id => selectedItems.includes(id));

                        return (
                            <Paper key={group.merchant_id} elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                {/* Header del Comercio */}
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} pb={2} borderBottom="1px solid #eee">
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <FontAwesomeIcon icon={faStore} style={{ color: '#1976d2' }} />
                                        <Typography variant="h6" fontWeight="bold">
                                            {group.merchant_name}
                                        </Typography>
                                    </Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                checked={allSelected} 
                                                indeterminate={someSelected && !allSelected}
                                                onChange={() => handleToggleMerchant(group.merchant_id, group.items)}
                                                color="primary"
                                            />
                                        }
                                        label={allSelected ? "Deseleccionar local" : "Seleccionar todo"}
                                        sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                                    />
                                </Box>

                                {/* Lista de Productos del Comercio */}
                                {group.items.map((item) => (
                                    <Box key={item.item_id} display="flex" alignItems="flex-start" gap={1} mb={2} 
                                        sx={{ 
                                            flexDirection: { xs: 'column', sm: 'row' }, // Apila en móviles
                                            border: { xs: '1px solid #f0f0f0', sm: 'none' }, // Borde sutil en móviles
                                            borderRadius: 2, p: { xs: 1.5, sm: 0 }
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={selectedItems.includes(item.item_id)} 
                                                    onChange={() => handleToggleItem(item.item_id)}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>Seleccionar ítem</Typography>}
                                            sx={{ m: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                                        />
                                        <Box flexGrow={1} width="100%">
                                            <CartItemRow 
                                                item={item} 
                                                onRemove={handleRemoveItem}
                                                onUpdateQty={handleUpdateQty}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        );
                    })}
                </Grid>

               {/* --- RESUMEN DE PAGO (DERECHA - OCULTO EN MÓVILES) --- */}
                <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: '#fff', border: '1px solid', borderColor: 'primary.main', borderRadius: 3, position: 'sticky', top: 100 }}>
                        <Typography variant="h6" fontWeight="bold" mb={3}>Resumen de Pago</Typography>
                        
                        <Box display="flex" justifyContent="space-between" mb={2}>
                            <Typography color="text.secondary">Subtotal ({selectedItems.length} ítems)</Typography>
                            <Typography fontWeight="600">{activeCurrencySymbol}{calcSubtotal.toFixed(2)}</Typography>
                        </Box>

                        {calcDelivery > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Typography color="text.secondary">Delivery Estimado</Typography>
                                <Typography fontWeight="600">{activeCurrencySymbol} {calcDelivery.toFixed(2)}</Typography>
                            </Box>
                        )}
                        
                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" justifyContent="space-between" mb={3} alignItems="flex-end">
                            <Typography variant="h6" fontWeight="bold">Total General</Typography>
                            <Box textAlign="right">
                                <Typography variant="caption" color="text.secondary">Moneda Local ({activeCurrencyCode})</Typography>
                                <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1 }}>
                                    {activeCurrencySymbol}{totalToPay.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>

                        <Button 
                            fullWidth variant="contained" size="large" onClick={handleCheckout}
                            disabled={processing || selectedItems.length === 0}
                            endIcon={(!processing && selectedItems.length > 0) && <FontAwesomeIcon icon={faArrowRight} />}
                            sx={{ py: 1.8, borderRadius: 3, fontWeight: 'bold', fontSize: '1.05rem', boxShadow: '0 8px 20px -4px rgba(0,118,255,0.4)' }}
                        >
                            {processing ? 'Cargando...' : selectedItems.length === 0 ? 'Selecciona productos' : 'Continuar'}
                        </Button>
                    </Paper>
                </Grid>
            </Grid>

            {/* --- BARRA FLOTANTE (SÓLO PARA MÓVILES) --- */}
            <Paper 
                elevation={8} 
                sx={{ 
                    display: { xs: 'block', md: 'none' }, 
                    position: 'fixed', bottom: 0, left: 0, right: 0, 
                    p: 2, pt: 1.5, zIndex: 1000, 
                    borderTopLeftRadius: 20, borderTopRightRadius: 20,
                    pb: 'calc(env(safe-area-inset-bottom) + 16px)' // Soporte para el "notch" inferior del iPhone
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {selectedItems.length} ítems seleccionados
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1 }}>
                            {activeCurrencySymbol}{totalToPay.toFixed(2)}
                        </Typography>
                    </Box>
                    <Button 
                        variant="contained" size="large" onClick={handleCheckout}
                        disabled={processing || selectedItems.length === 0}
                        sx={{ borderRadius: 50, px: 4, fontWeight: 'bold', minWidth: 150 }}
                    >
                        {processing ? 'Carg.' : 'Continuar'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
}